import { getLocalizedRouteMap } from "./localized-routes/get-localized-route-map"
import { getCleanPathname } from "./path"

export const DEFAULT_LOCALE = "en"
export const ALTERNATIVE_LOCALES = ["de"] as const
export type AlternativeLocale = (typeof ALTERNATIVE_LOCALES)[number]
export const LOCALES = [DEFAULT_LOCALE, ...ALTERNATIVE_LOCALES] as const
export type Locale = (typeof LOCALES)[number]

export function isValidLocale(locale: unknown): locale is Locale {
  return LOCALES.includes(locale as Locale)
}

export function isAlternativeLocale(
  locale: unknown,
): locale is AlternativeLocale {
  return ALTERNATIVE_LOCALES.includes(locale as AlternativeLocale)
}

export function getLocaleFromPathname(pathname: string): Locale {
  const cleanPathname = getCleanPathname(pathname)
  const locale = cleanPathname.split("/")[1]
  if (isValidLocale(locale)) {
    return locale
  }

  return DEFAULT_LOCALE
}

export const getNonLocalizedPathname = (
  pathname: string,
  locale?: Locale,
): string => {
  const cleanPathname = getCleanPathname(pathname)
  const resolvedLocale = locale ?? getLocaleFromPathname(cleanPathname)
  const [, ...pathParts] = cleanPathname.split("/") // Skips the empty string at the beginning
  if (pathParts[0] === resolvedLocale) {
    return `/${pathParts.slice(1).join("/")}`
  }
  return cleanPathname
}

export function getLocalizedPagePath(
  pathname: string,
  locale: Locale,
): string | undefined {
  const cleanPathname = getCleanPathname(pathname)
  const nonLocalizedPathname = getNonLocalizedPathname(cleanPathname)

  if (locale === DEFAULT_LOCALE) {
    return nonLocalizedPathname
  }
  const localizedRoutes = getLocalizedRouteMap()
  if (localizedRoutes?.[nonLocalizedPathname]?.includes(locale)) {
    return `/${locale}${nonLocalizedPathname}`
  }

  return undefined
}
